import { RefObject, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { OpenPublishingTaskResult } from "services/tasks/models/tasksTypes";
import { ScientificPublicationsService } from "services/scientificPublicationsService";
import { WorkFlowService } from "services/workFlowService";
import { useNotification } from "hooks/useNotification";
import { tabsIds } from "../../orderPage.types";
import { ScientificPublicationFormRef } from "../../tabs/OrderInfoTab/components/ScientificPublicationForm";

type ActionsProps = {
  selectedTabId: string;
  openTask: OpenPublishingTaskResult | null;
  submitRef: RefObject<ScientificPublicationFormRef>;
};

const Actions = ({ selectedTabId, openTask, submitRef }: ActionsProps) => {
  const { t } = useTranslation("Orders");
  const navigate = useNavigate();
  const { serialGID } = useParams();
  const { showNotification } = useNotification();
  const isSubmittingRef = useRef(false);

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;

    if (!submitRef.current) {
      showNotification({
        description: t("Common:formNotReady"),
        type: "error",
      });
      return;
    }

    // Validate form first
    const isValid = await submitRef.current.isValid();
    if (!isValid) {
      showNotification({
        description: t("Common:pleaseFixValidationErrors"),
        type: "error",
      });
      return;
    }

    isSubmittingRef.current = true;

    try {
      // Get form data
      const formData = submitRef.current.getFormData();

      // Step 1: Create Scientific Publication
      const response = await ScientificPublicationsService.createScientificPublication(formData);

      if (response?.hasError) {
        showNotification({
          description: t("Common:internalServerError"),
          type: "error",
        });
        isSubmittingRef.current = false;
        return;
      }

      // Step 2: Call workflow action
      if (openTask && serialGID) {
        const workflowResponse = await WorkFlowService.WorkflowAction(
          openTask.courseOrderId ?? 0,
          {
            actionName: "Complete",
            serialGuid: serialGID,
            activityGuid: openTask.activityGuid ?? "",
            notes: "",
            filesIds: [],
          }
        );

        if (workflowResponse.hasError) {
          showNotification({
            description: t("Common:workflowActionFailed"),
            type: "error",
          });
          isSubmittingRef.current = false;
          return;
        }
      }

      showNotification({
        description: t("Common:success"),
        type: "success",
      });
      navigate("/home/tasks");
    } catch (error) {
      showNotification({
        description: t("Common:internalServerError"),
        type: "error",
      });
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <>
      {selectedTabId === tabsIds.info && (
        <button
          className="btn btn-primary px-3"
          type="button"
          onClick={handleSubmit}
          disabled={isSubmittingRef.current}
        >
          {isSubmittingRef.current && (
            <span className="spinner-border spinner-border-sm mx-2" role="status" aria-hidden="true" />
          )}
          {t("Common:save")}
        </button>
      )}
    </>
  );
};

export default Actions;
